import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useTransactions } from '../../hooks/useTransactions';
import supabase from '../../supabase/supabase';

const {
  FiDollarSign, FiSearch, FiRefreshCw, FiCheckCircle, FiXCircle, 
  FiClock, FiCornerDownRight, FiCreditCard, FiSend, FiAlertCircle, FiLoader
} = FiIcons;

const STATUS_COLORS = {
  pending: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  completed: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  refunded: 'text-zinc-400 bg-zinc-500/10 border-zinc-500/20',
  failed: 'text-red-400 bg-red-500/10 border-red-500/20',
  disputed: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
};

const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency,
    minimumFractionDigits: 2
  }).format((amount || 0) / 100);
};

export default function TransactionManager() {
  const [activeView, setActiveView] = useState('transactions'); // 'transactions' | 'payouts'
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [refundReason, setRefundReason] = useState('');
  const [processingRefund, setProcessingRefund] = useState(false);
  
  // Payout states
  const [payoutsData, setPayoutsData] = useState([]);
  const [loadingPayouts, setLoadingPayouts] = useState(false);
  const [payoutError, setPayoutError] = useState(null);

  const { transactions, loading, error, refetch, refundTransaction } = useTransactions({
    status: statusFilter === 'all' ? undefined : statusFilter,
  });

  const fetchPayouts = async () => {
    setLoadingPayouts(true);
    setPayoutError(null);
    try {
      // Mock logic: Fetch artists and calculate their pending balances from transactions
      const { data: artists, error: artistsError } = await supabase.from('d2f_artists').select('id, name, payout_threshold');
      if (artistsError) throw artistsError;
      
      const { data: txs, error: txsError } = await supabase.from('d2f_transactions').select('artist_id, amount').eq('status', 'completed');
      if (txsError) throw txsError;
      
      const { data: pastPayouts, error: payoutsError } = await supabase.from('d2f_payouts').select('artist_id, amount').eq('status', 'paid');
      if (payoutsError) throw payoutsError;
      
      const balances = (artists || []).map(artist => {
        const totalEarned = (txs || []).filter(t => t.artist_id === artist.id).reduce((sum, t) => sum + Number(t.amount || 0), 0);
        const totalPaid = (pastPayouts || []).filter(p => p.artist_id === artist.id).reduce((sum, p) => sum + Number(p.amount || 0), 0);
        const pendingValue = totalEarned - totalPaid;
        
        return {
          ...artist,
          pending: pendingValue,
          isEligible: pendingValue >= ((artist.payout_threshold || 50) * 100) // threshold in dollars, pending in cents
        };
      }).filter(a => a.pending > 0);
      
      setPayoutsData(balances);
    } catch (err) {
      console.error('Failed to fetch payouts', err);
      setPayoutError(err.message);
    } finally {
      setLoadingPayouts(false);
    }
  };

  useEffect(() => {
    if (activeView === 'payouts') fetchPayouts();
  }, [activeView]);

  const handleRunPayouts = async () => {
    if(!window.confirm("This will initiate payouts to all eligible artists via Stripe Connect. Proceed?")) return;
    setLoadingPayouts(true);
    try {
      // Create payout records for eligible artists
      const eligible = payoutsData.filter(a => a.isEligible);
      for(const artist of eligible) {
        await supabase.from('d2f_payouts').insert({
          artist_id: artist.id,
          amount: artist.pending,
          status: 'paid',
          metadata: { note: 'Automated Cycle Payout' }
        });
      }
      alert(`Successfully processed ${eligible.length} payouts.`);
      fetchPayouts();
    } catch (e) {
      console.error(e);
      alert('Payout process failed: ' + e.message);
    } finally {
      setLoadingPayouts(false);
    }
  };

  const filteredTransactions = (transactions || []).filter(t => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      t.user?.email?.toLowerCase().includes(searchLower) ||
      t.user?.display_name?.toLowerCase().includes(searchLower) ||
      t.drop?.title?.toLowerCase().includes(searchLower) ||
      t.id?.toLowerCase().includes(searchLower)
    );
  });

  const handleRefund = async () => {
    if (!selectedTransaction || !refundReason.trim()) return;
    setProcessingRefund(true);
    const result = await refundTransaction(selectedTransaction.id, refundReason.trim());
    setProcessingRefund(false);
    if (result.success) {
      setSelectedTransaction(null);
      setRefundReason('');
    } else {
      alert(result.error || 'Failed to process refund');
    }
  };

  return (
    <div className="space-y-6">
      {/* View Toggle */}
      <div className="flex border-b border-neutral-800 mb-6">
        <button
          onClick={() => setActiveView('transactions')}
          className={`pb-4 px-6 font-medium text-sm transition-colors border-b-2 ${activeView === 'transactions' ? 'border-indigo-500 text-white' : 'border-transparent text-neutral-500 hover:text-neutral-300'}`}
        >
          <SafeIcon icon={FiDollarSign} className="inline mr-2" /> All Transactions
        </button>
        <button
          onClick={() => setActiveView('payouts')}
          className={`pb-4 px-6 font-medium text-sm transition-colors border-b-2 ${activeView === 'payouts' ? 'border-indigo-500 text-white' : 'border-transparent text-neutral-500 hover:text-neutral-300'}`}
        >
          <SafeIcon icon={FiCreditCard} className="inline mr-2" /> Automated Payouts
        </button>
      </div>

      {activeView === 'transactions' && (
        <>
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start gap-3">
              <SafeIcon icon={FiAlertCircle} className="text-red-400 text-xl mt-0.5" />
              <div>
                <h4 className="text-red-400 font-medium">Error Loading Transactions</h4>
                <p className="text-red-300 text-sm">{error}</p>
                <button onClick={refetch} className="mt-2 text-xs text-red-400 hover:text-red-300 underline">Retry</button>
              </div>
            </div>
          )}
          
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[300px]">
              <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by email, name, or transaction ID..."
                className="w-full pl-11 pr-4 py-2 bg-black border border-neutral-800 rounded-lg text-sm text-white focus:border-indigo-500"
              />
            </div>
            <div className="flex items-center gap-3">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 bg-black border border-neutral-800 rounded-lg text-sm text-white focus:border-indigo-500"
              >
                <option value="all">All Statuses</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="refunded">Refunded</option>
                <option value="failed">Failed</option>
              </select>
              <button onClick={refetch} className="flex items-center gap-2 px-4 py-2 bg-neutral-800 rounded-lg text-sm text-white hover:bg-neutral-700">
                <SafeIcon icon={FiRefreshCw} className={loading ? 'animate-spin' : ''} /> Refresh
              </button>
            </div>
          </div>

          <div className="bg-neutral-900 border border-neutral-800 rounded-xl overflow-hidden">
            <table className="w-full text-left text-sm">
              <thead className="bg-black/50 text-neutral-400">
                <tr>
                  <th className="px-6 py-4 font-medium">Transaction</th>
                  <th className="px-6 py-4 font-medium">Customer</th>
                  <th className="px-6 py-4 font-medium">Amount</th>
                  <th className="px-6 py-4 font-medium">Status</th>
                  <th className="px-6 py-4 font-medium">Date</th>
                  <th className="px-6 py-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {loading ? (
                  <tr><td colSpan={6} className="px-6 py-8 text-center text-neutral-500"><SafeIcon icon={FiLoader} className="animate-spin inline mr-2"/> Loading transactions...</td></tr>
                ) : filteredTransactions.length === 0 ? (
                  <tr><td colSpan={6} className="px-6 py-12 text-center text-neutral-500">No transactions found</td></tr>
                ) : filteredTransactions.map((t) => (
                  <tr key={t.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4">
                      <p className="text-xs font-mono text-neutral-400">{t.id?.slice(0, 8)}...</p>
                      <p className="text-xs text-neutral-600 capitalize">{t.payment_method || 'Unknown'}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium text-white">{t.user?.display_name || 'Unknown'}</p>
                      <p className="text-xs text-neutral-500">{t.user?.email || 'No email'}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-bold text-white">{formatCurrency(t.amount, t.currency)}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${STATUS_COLORS[t.status] || STATUS_COLORS.pending}`}>
                        {t.status || 'pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-neutral-400">{new Date(t.created_at).toLocaleDateString()}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {t.status === 'completed' && (
                        <button onClick={() => setSelectedTransaction(t)} className="text-xs font-medium text-red-400 hover:text-red-300 border border-red-500/20 px-3 py-1.5 rounded-lg">
                          Refund
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeView === 'payouts' && (
        <div className="space-y-6 animate-fade-in">
          {payoutError && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start gap-3">
              <SafeIcon icon={FiAlertCircle} className="text-red-400 text-xl mt-0.5" />
              <div>
                <h4 className="text-red-400 font-medium">Error Loading Payouts</h4>
                <p className="text-red-300 text-sm">{payoutError}</p>
                <button onClick={fetchPayouts} className="mt-2 text-xs text-red-400 hover:text-red-300 underline">Retry</button>
              </div>
            </div>
          )}
          
          <div className="flex justify-between items-center bg-indigo-500/10 border border-indigo-500/20 p-6 rounded-xl">
            <div>
              <h3 className="text-lg font-bold text-indigo-400 mb-1">Automated Artist Payouts</h3>
              <p className="text-sm text-indigo-400/70">Process pending earnings to connected Stripe accounts based on artist minimum thresholds.</p>
            </div>
            <button 
              onClick={handleRunPayouts} 
              disabled={loadingPayouts || payoutsData.filter(a => a.isEligible).length === 0}
              className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-colors disabled:opacity-50"
            >
              <SafeIcon icon={loadingPayouts ? FiLoader : FiSend} className={loadingPayouts ? 'animate-spin' : ''} /> 
              Run Payout Cycle
            </button>
          </div>

          <div className="bg-neutral-900 border border-neutral-800 rounded-xl overflow-hidden">
            <table className="w-full text-left text-sm">
              <thead className="bg-black/50 text-neutral-400">
                <tr>
                  <th className="px-6 py-4 font-medium">Artist</th>
                  <th className="px-6 py-4 font-medium">Threshold</th>
                  <th className="px-6 py-4 font-medium">Pending Balance</th>
                  <th className="px-6 py-4 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {loadingPayouts ? (
                  <tr><td colSpan={4} className="px-6 py-8 text-center text-neutral-500"><SafeIcon icon={FiLoader} className="animate-spin inline mr-2"/> Calculating balances...</td></tr>
                ) : payoutsData.length === 0 ? (
                  <tr><td colSpan={4} className="px-6 py-12 text-center text-neutral-500">No artists have pending balances</td></tr>
                ) : payoutsData.map((artist) => (
                  <tr key={artist.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4 font-medium text-white">{artist.name || 'Unknown Artist'}</td>
                    <td className="px-6 py-4 text-neutral-400 font-mono">${artist.payout_threshold || 50}</td>
                    <td className="px-6 py-4 font-bold text-white">{formatCurrency(artist.pending)}</td>
                    <td className="px-6 py-4">
                      {artist.isEligible ? (
                         <span className="text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded text-xs font-medium border border-emerald-500/20">Ready for Payout</span>
                      ) : (
                         <span className="text-amber-400 bg-amber-500/10 px-2 py-1 rounded text-xs font-medium border border-amber-500/20">Below Threshold</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {selectedTransaction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-md bg-neutral-900 border border-neutral-800 rounded-2xl p-6 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-white">Process Refund</h3>
              <button onClick={() => setSelectedTransaction(null)} className="text-neutral-500 hover:text-white"><SafeIcon icon={FiXCircle} className="text-xl" /></button>
            </div>
            <div className="p-4 bg-black rounded-lg mb-4 text-sm text-neutral-300">
              <p>Customer: <strong>{selectedTransaction.user?.display_name || 'Unknown'}</strong></p>
              <p>Amount: <strong>{formatCurrency(selectedTransaction.amount, selectedTransaction.currency)}</strong></p>
            </div>
            <textarea
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              placeholder="Reason for refund..."
              className="w-full bg-black border border-neutral-800 rounded-xl p-3 text-sm text-white focus:border-red-500 mb-6"
              rows={3}
            />
            <div className="flex gap-3">
              <button onClick={() => setSelectedTransaction(null)} className="flex-1 py-2 rounded-lg border border-neutral-700 text-neutral-400 hover:text-white">Cancel</button>
              <button onClick={handleRefund} disabled={processingRefund || !refundReason.trim()} className="flex-1 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium disabled:opacity-50">
                {processingRefund ? 'Processing...' : 'Confirm Refund'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}